import { Form, Formik, FormikProps } from 'formik'
import * as Yup from 'yup'
import React from 'react'
import { formatError } from 'utils'

import { ErrorIndicator, Stepper } from 'components'
import { Flex, Text, Image, Box, useMediaQuery, Heading, VStack } from '@chakra-ui/react'

import { PrimaryButton, SecondaryButton } from 'components/Buttons'

import AgencyInfo from './components/AgencyInfo'
import SuperAdminInfo from './components/SuperAdminInfo'
import { images } from 'theme'
import { useAuthContext } from 'context/AuthProvider'
import { useHistory } from 'react-router'
import Confirm from './components/Confirm'
import { useTranslation } from 'react-i18next'

type RouteType = {
  label: string
  component: any
  key: string
}
export type RegisterAgencyValues = {
  agencyName: string
  agencyEmail: string
  agencyPhone: string
  agencyAddress: string
  agencyLogo: any
  agencyWebsite: string
  agencyDescription: string
  adminFirstName: string
  adminLastName: string
  adminEmail: string
  adminPhone: string
  adminPassword: string
  adminConfirmPassword: string
  adminAgentNumber: string
  adminProfilePicture: any
}

const RegisterAgency: React.FC = () => {
  const [propertyCreationStatus, setPropertyCreationStatus] = React.useState<
    'images' | 'property' | 'success' | 'error' | null
  >(null)
  const { user, logout, register } = useAuthContext()
  const { t } = useTranslation()
  const history = useHistory()
  const stepInfo = [
    t('signup.agency.agency_info'),
    t('signup.agency.admin_info'),
    t('signup.agency.confirm')
  ]

  const urlParams = new URLSearchParams(window.location.search)

  const agancyName = urlParams.get('agancyName')
  const agancyEmail = urlParams.get('agancyEmail')
  const agancyPhone = urlParams.get('agancyPhone')
  const agancyAddress = urlParams.get('agancyAddress')
  const website = urlParams.get('website')

  const [isTabletOrMobile] = useMediaQuery('(max-width: 40em)')
  React.useEffect(() => {
    if (user?.confirmed) {
      history.push('/auth/dashboard/agent/team-management')
    } else if (user) {
      history.push('/confirm-email')
      logout && logout()
    }
    // eslint-disable-next-line
  }, [user])
  const initialValues: RegisterAgencyValues = {
    agencyName: agancyName || '',
    agencyEmail: agancyEmail || '',
    agencyPhone: agancyPhone || '',
    agencyAddress: agancyAddress || '',
    agencyLogo: null,
    agencyWebsite: website || '',
    agencyDescription: '',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminPhone: '',
    adminPassword: '',
    adminConfirmPassword: '',
    adminAgentNumber: '',
    adminProfilePicture: null
  }

  const [activeStep, setActiveStep] = React.useState(0)
  const validateAddPropertySchema = Yup.object().shape({
    ...(activeStep === 0 && {
      agencyName: Yup.string().required('Agency name is required'),
      agencyEmail: Yup.string().required('Agency email is required'),
      agencyPhone: Yup.string().required('Agency phone is required'),
      agencyAddress: Yup.string().required('Agency address is required'),
      agencyLogo: Yup.mixed().required('Agency logo is required'),
      agencyWebsite: Yup.string().required('Agency website is required'),
      agencyDescription: Yup.string().required('Agency description is required')
    }),
    ...(activeStep === 1 && {
      adminFirstName: Yup.string().required('Admin first name is required'),
      adminLastName: Yup.string().required('Admin last name is required'),
      adminEmail: Yup.string().required('Admin email is required'),
      adminPhone: Yup.string().required('Admin phone is required'),
      adminPassword: Yup.string().required('Admin password is required'),
      adminConfirmPassword: Yup.string()
        .oneOf([Yup.ref('adminPassword')], 'Password does not match')
        .required('Admin confirm password is required'),
      adminAgentNumber: Yup.string().required('Admin agent number is required'),
      adminProfilePicture: Yup.mixed().required('Admin profile picture is required')
    })
  })

  const routes: RouteType[] = [
    {
      label: 'Agency Info',
      component: <AgencyInfo />,
      key: 'Agency Info'
    },
    {
      label: 'Super Admin Info',
      component: <SuperAdminInfo />,
      key: 'Super Admin Info'
    },
    {
      label: 'Confirm',
      component: <Confirm isTabletOrMobile={isTabletOrMobile} />,
      key: 'Confirm'
    }
  ]

  return (
    <Flex direction={isTabletOrMobile ? 'column' : 'row'}>
      <Formik
        validationSchema={validateAddPropertySchema}
        initialValues={initialValues}
        validateOnMount
        onSubmit={async (
          {
            agencyName,
            agencyEmail,
            agencyPhone,
            agencyAddress,
            agencyLogo,
            agencyWebsite,
            agencyDescription,
            adminFirstName,
            adminLastName,
            adminEmail,
            adminPhone,
            adminConfirmPassword,
            adminAgentNumber,
            adminProfilePicture
          },
          { setStatus, setSubmitting }
        ) => {
          setStatus(null)

          setSubmitting(true)
          try {
            if (register) {
              const fullName = `${adminFirstName} ${adminLastName} - ${adminAgentNumber}`

              await register(
                fullName,
                adminFirstName,
                adminLastName,
                adminEmail,
                adminConfirmPassword,
                adminPhone,
                {
                  role: 'admin',
                  payload: {
                    agencyName,
                    agencyEmail,
                    agencyPhone,
                    agencyAddress,
                    agencyLogo,
                    agencyWebsite,
                    agencyDescription,
                    adminAgentNumber,
                    adminProfilePicture
                  }
                }
              )
            }
            setPropertyCreationStatus('success')
            setSubmitting(false)
          } catch (error: any) {
            setPropertyCreationStatus('error')
            console.log(error)
            setStatus(formatError(error))
          }
        }}
      >
        {({
          status,
          isSubmitting,
          handleSubmit,
          isValid,
          validateForm,
          values
        }: FormikProps<RegisterAgencyValues>) => (
          <Form style={{ width: '100%', flex: 1 }} autoComplete="off">
            <VStack my={8} spacing={0}>
              <Heading as="h3" fontWeight="extrabold">
                {t('signup.agency.tilte')}
              </Heading>
              <Text fontSize="xs" color="gray.500">
                {t('signup.agency.sub_title')}
              </Text>
            </VStack>
            <Stepper
              stepInfo={stepInfo}
              p={0}
              mt={6}
              mb={12}
              justifyContent="flex-start"
              activeStep={activeStep}
            >
              {routes.map((route) => (
                <Box key={route.key}>
                  {route.component}
                  {status && <ErrorIndicator status={status} />}
                  <Flex
                    direction={isTabletOrMobile ? 'column-reverse' : 'row'}
                    align={isTabletOrMobile ? 'flex-start' : 'center'}
                    mt={8}
                  >
                    {!(activeStep === 0) && (
                      <SecondaryButton
                        onClick={async () => {
                          await validateForm(values)
                          setActiveStep((prev) => prev - 1)
                        }}
                        mr={8}
                        minW={isTabletOrMobile ? '100%' : undefined}
                        disabled={activeStep === 0}
                      >
                        {activeStep === 0 ? t('signup.agency.cancel') : t('signup.agency.back')}
                      </SecondaryButton>
                    )}
                    <PrimaryButton
                      my={8}
                      mr={4}
                      minW={isTabletOrMobile ? '100%' : 52}
                      isDisabled={isSubmitting || !isValid}
                      onClick={
                        activeStep === routes.length - 1
                          ? () => handleSubmit()
                          : async () => {
                              if (isValid) {
                                setActiveStep((prev) => prev + 1)
                              } else {
                                validateForm()
                              }
                            }
                      }
                      type={activeStep === routes.length - 1 ? 'submit' : 'button'}
                      colorScheme="orange"
                      isLoading={isSubmitting}
                    >
                      {activeStep === routes.length - 1
                        ? t('signup.agency.signup_btn')
                        : t('signup.agency.next')}
                    </PrimaryButton>
                    {propertyCreationStatus && (
                      <Text fontSize="x-small" color="gray.500">
                        Creating {propertyCreationStatus} ...
                      </Text>
                    )}
                  </Flex>
                </Box>
              ))}
            </Stepper>
          </Form>
        )}
      </Formik>
      <Box flex={1.2}>
        <Image src={images.agentIlu2} w="60%" />
      </Box>
    </Flex>
  )
}

export default RegisterAgency
